import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadingSpinner, Navbar } from '../components';
import { eventService, mimeService, applicationService } from '../services';
import { useAuth, useUser } from '../context';

/**
 * MimePage - Dedicated MIME (Make It More Epic) creation page
 * Flow: Host edits event → adjusts commitment → writes message → sets response window → preview → send
 * Design: Dark theme matching the mockup
 */
export default function MimePage() {
  const navigate = useNavigate();
  const { id: eventId } = useParams();
  const { isAuthenticated, logout } = useAuth();
  const { profile, walletBalance } = useUser();

  // Event data
  const [event, setEvent] = useState(null);
  const [acceptedCount, setAcceptedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // MIME form state
  const [commitmentMode, setCommitmentMode] = useState('keep'); // 'keep' or 'add'
  const [additionalAmount, setAdditionalAmount] = useState(10);
  const [message, setMessage] = useState('');
  const [responseWindowMinutes, setResponseWindowMinutes] = useState(1440); // 24 hours default

  // Preview & submission
  const [showPreview, setShowPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Check for existing active round
  const [hasActiveRound, setHasActiveRound] = useState(false);

  const fetchEventData = useCallback(async () => {
    if (!eventId) return;

    try {
      const [eventResult, appsResult, summaryResult] = await Promise.all([
        eventService.getEvent(eventId),
        applicationService.getEventApplications(eventId),
        mimeService.getEventSummary(eventId),
      ]);

      if (eventResult.success) {
        setEvent(eventResult.data);
      } else {
        setError('Event not found');
        return;
      }

      if (appsResult.success) {
        const accepted = (appsResult.data || []).filter(
          (a) => a.status === 'accepted' || a.status === 'paid'
        );
        setAcceptedCount(accepted.length);
      }

      if (summaryResult.success && summaryResult.data?.activeRound) {
        setHasActiveRound(true);
      }
    } catch (err) {
      setError('Failed to load event data');
    } finally {
      setIsLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchEventData();
    }
  }, [isAuthenticated, fetchEventData]);

  const formatAmount = (cents) => `$${(cents / 100).toFixed(2)}`;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    const additionalCommitmentCents = commitmentMode === 'keep' ? 0 : Math.round(additionalAmount * 100);

    try {
      const result = await mimeService.createRound(eventId, {
        additionalCommitmentCents,
        message: message.trim() || null,
        shotClockMinutes: responseWindowMinutes,
        removeNonResponders: false,
      });

      if (result.success) {
        navigate(`/host/events/${eventId}`);
      } else {
        setSubmitError(result.error || 'Failed to send MIME');
      }
    } catch (err) {
      setSubmitError('An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const responseWindowOptions = [
    { value: 30, label: '30 minutes' },
    { value: 60, label: '1 hour' },
    { value: 120, label: '2 hours' },
    { value: 360, label: '6 hours' },
    { value: 720, label: '12 hours' },
    { value: 1440, label: '24 hours' },
  ];

  const getResponseWindowLabel = (minutes) => {
    const opt = responseWindowOptions.find((o) => o.value === minutes);
    return opt ? opt.label : `${minutes} minutes`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <LoadingSpinner label="Loading..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar
        mode="host"
        onModeChange={(mode) => navigate(mode === 'guest' ? '/guest' : mode === 'featured' ? '/featured' : '/host')}
        user={profile}
        walletBalance={walletBalance}
        onLogout={logout}
      />
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <p className="font-mono text-sm text-red-400">{error}</p>
          <button
            onClick={() => navigate(`/host/events/${eventId}`)}
            className="mt-4 font-mono text-sm text-white underline"
          >
            Back to event
          </button>
        </div>
      </div>
    );
  }

  if (hasActiveRound) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar
        mode="host"
        onModeChange={(mode) => navigate(mode === 'guest' ? '/guest' : mode === 'featured' ? '/featured' : '/host')}
        user={profile}
        walletBalance={walletBalance}
        onLogout={logout}
      />
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <span className="inline-block px-3 py-1 bg-white text-black font-mono text-xs font-bold uppercase tracking-wider mb-4">
            MIME
          </span>
          <p className="font-mono text-sm text-gray-300">
            An active MIME round is already in progress. Complete or cancel it before creating a new one.
          </p>
          <button
            onClick={() => navigate(`/host/events/${eventId}`)}
            className="mt-6 font-mono text-sm text-white underline"
          >
            Back to event dashboard
          </button>
        </div>
      </div>
    );
  }

  // Preview view
  if (showPreview) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar
        mode="host"
        onModeChange={(mode) => navigate(mode === 'guest' ? '/guest' : mode === 'featured' ? '/featured' : '/host')}
        user={profile}
        walletBalance={walletBalance}
        onLogout={logout}
      />
        <div className="max-w-lg mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <span className="inline-block px-3 py-1 bg-white text-black font-mono text-xs font-bold uppercase tracking-wider mb-3">
              MIME
            </span>
            <h1 className="font-mono text-2xl font-bold text-white mb-2">
              Preview.
            </h1>
            <p className="font-mono text-sm text-gray-400">
              This is what your {acceptedCount} committed guest{acceptedCount !== 1 ? 's' : ''} will receive.
            </p>
          </div>

          {/* Preview Card */}
          <div className="border border-gray-700 p-6 mb-6">
            <p className="font-mono text-xs text-gray-500 uppercase tracking-wider mb-2">EVENT</p>
            <p className="font-mono text-lg font-bold text-white mb-1">{event?.title}</p>
            <p className="font-mono text-sm text-gray-400">
              {formatAmount(event?.ticketPriceCents || 0)} · {acceptedCount} committed
            </p>
          </div>

          {/* Commitment Change */}
          <div className="border border-gray-700 p-6 mb-6">
            <p className="font-mono text-xs text-gray-500 uppercase tracking-wider mb-2">COMMITMENT CHANGE</p>
            {commitmentMode === 'keep' ? (
              <p className="font-mono text-lg font-bold text-white">
                No change — keep {formatAmount(event?.ticketPriceCents || 0)}
              </p>
            ) : (
              <p className="font-mono text-lg font-bold text-white">
                +${additionalAmount.toFixed(0)} additional commitment
              </p>
            )}
          </div>

          {/* Message */}
          {message.trim() && (
            <div className="border border-gray-700 p-6 mb-6">
              <p className="font-mono text-xs text-gray-500 uppercase tracking-wider mb-2">MESSAGE</p>
              <p className="font-mono text-sm text-gray-300">{message}</p>
            </div>
          )}

          {/* Response Window */}
          <div className="border border-gray-700 p-6 mb-6">
            <p className="font-mono text-xs text-gray-500 uppercase tracking-wider mb-2">RESPONSE WINDOW</p>
            <p className="font-mono text-lg font-bold text-white">
              {getResponseWindowLabel(responseWindowMinutes)}
            </p>
          </div>

          {/* Refund Notice */}
          <div className="border border-gray-700 p-4 mb-8 bg-gray-900">
            <p className="font-mono text-xs text-gray-400">
              You changed the deal. Guests who withdraw during the response window get a full refund, no flake fee.
            </p>
          </div>

          {submitError && (
            <p className="font-mono text-xs text-red-400 mb-4">{submitError}</p>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={() => setShowPreview(false)}
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 border-2 border-gray-600 text-gray-300 font-mono text-sm font-semibold uppercase tracking-wider hover:border-white hover:text-white transition-colors disabled:opacity-50"
            >
              BACK
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-white text-black border-2 border-white font-mono text-sm font-semibold uppercase tracking-wider hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'SENDING...' : 'SEND MIME'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main form view
  return (
    <div className="min-h-screen bg-black">
      <Navbar
        mode="host"
        onModeChange={(mode) => navigate(mode === 'guest' ? '/guest' : mode === 'featured' ? '/featured' : '/host')}
        user={profile}
        walletBalance={walletBalance}
        onLogout={logout}
      />
      <div className="max-w-lg mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <span className="inline-block px-3 py-1 bg-white text-black font-mono text-xs font-bold uppercase tracking-wider mb-3">
            MIME
          </span>
          <h1 className="font-mono text-2xl font-bold text-white mb-2">
            The event changed.
          </h1>
          <p className="font-mono text-sm text-gray-400">
            Update your event, adjust the price if needed, and let your {acceptedCount} committed guest{acceptedCount !== 1 ? 's' : ''} know.
          </p>
        </div>

        {/* Event Info Card */}
        <div className="border border-gray-700 p-6 mb-6">
          <p className="font-mono text-lg font-bold text-white mb-1">
            {event?.title}
          </p>
          <p className="font-mono text-sm text-gray-400">
            {formatAmount(event?.ticketPriceCents || 0)} · {acceptedCount} committed
          </p>

          <button
            onClick={() => navigate(`/host/events/${eventId}/edit`)}
            className="mt-4 inline-flex items-center font-mono text-sm text-white font-semibold uppercase tracking-wider underline underline-offset-4 hover:text-gray-300 transition-colors"
          >
            EDIT EVENT PAGE &rarr;
          </button>
        </div>

        {/* Commitment Change */}
        <div className="mb-6">
          <p className="font-mono text-xs text-gray-500 uppercase tracking-wider font-bold mb-3">
            COMMITMENT CHANGE
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setCommitmentMode('keep')}
              className={`flex-1 px-4 py-3 border-2 font-mono text-sm font-semibold uppercase tracking-wider transition-colors ${
                commitmentMode === 'keep'
                  ? 'border-white bg-white text-black'
                  : 'border-gray-600 text-gray-400 hover:border-gray-400'
              }`}
            >
              KEEP {formatAmount(event?.ticketPriceCents || 0)}
            </button>
            <button
              onClick={() => setCommitmentMode('add')}
              className={`flex-1 px-4 py-3 border-2 font-mono text-sm font-semibold uppercase tracking-wider transition-colors ${
                commitmentMode === 'add'
                  ? 'border-white bg-white text-black'
                  : 'border-gray-600 text-gray-400 hover:border-gray-400'
              }`}
            >
              ADD $
            </button>
          </div>

          {/* Additional amount input */}
          {commitmentMode === 'add' && (
            <div className="mt-3">
              <div className="flex items-center border-2 border-gray-600 bg-transparent">
                <span className="px-4 py-3 font-mono text-sm text-gray-500 border-r border-gray-600">$</span>
                <input
                  type="number"
                  value={additionalAmount}
                  onChange={(e) => setAdditionalAmount(Math.max(1, Math.min(1000, parseFloat(e.target.value) || 0)))}
                  min={1}
                  max={1000}
                  step={1}
                  className="flex-1 px-4 py-3 bg-transparent text-white font-mono text-sm focus:outline-none"
                />
              </div>
              <p className="font-mono text-xs text-gray-600 mt-1">
                Between $1 and $1,000
              </p>
            </div>
          )}
        </div>

        {/* Message to Guests */}
        <div className="mb-6">
          <p className="font-mono text-xs text-gray-500 uppercase tracking-wider font-bold mb-3">
            MESSAGE TO GUESTS
          </p>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Hey everyone — the event just got bigger..."
            maxLength={500}
            rows={4}
            className="w-full px-4 py-3 border-2 border-gray-600 bg-transparent text-white font-mono text-sm placeholder-gray-600 focus:outline-none focus:border-gray-400 resize-none"
          />
          <p className="font-mono text-xs text-gray-600 mt-1">
            Sent to all {acceptedCount} committed guest{acceptedCount !== 1 ? 's' : ''} with your updated event page.
          </p>
        </div>

        {/* Response Window */}
        <div className="mb-6">
          <p className="font-mono text-xs text-gray-500 uppercase tracking-wider font-bold mb-3">
            RESPONSE WINDOW
          </p>
          <select
            value={responseWindowMinutes}
            onChange={(e) => setResponseWindowMinutes(parseInt(e.target.value))}
            className="w-full px-4 py-3 border-2 border-gray-600 bg-black text-white font-mono text-sm focus:outline-none focus:border-gray-400 appearance-none cursor-pointer"
          >
            {responseWindowOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="font-mono text-xs text-gray-600 mt-2">
            Guests see updated event details first before any commitment changes.
          </p>
        </div>

        {/* Refund Notice */}
        <div className="border border-gray-700 p-4 mb-8 bg-gray-900">
          <p className="font-mono text-xs text-gray-400">
            You changed the deal. Guests who withdraw during the response window get a full refund, no flake fee.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate(`/host/events/${eventId}`)}
            className="flex-1 px-6 py-3 border-2 border-gray-600 text-gray-300 font-mono text-sm font-semibold uppercase tracking-wider hover:border-white hover:text-white transition-colors"
          >
            CANCEL
          </button>
          <button
            onClick={() => setShowPreview(true)}
            disabled={acceptedCount === 0}
            className="flex-1 px-6 py-3 bg-white text-black border-2 border-white font-mono text-sm font-semibold uppercase tracking-wider hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            PREVIEW &rarr;
          </button>
        </div>
      </div>
    </div>
  );
}
